import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { Seo, JsonLd } from "../components/Seo";
import { Nav } from "../components/landing/Nav";
import { Footer } from "../components/landing/Footer";
import { ARTICLES, getArticle } from "../data/articles";

const ArticlePage = () => {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) {
    return (
      <div className="bg-navy text-ink min-h-screen antialiased" data-testid="article-not-found">
        <Nav />
        <main className="max-w-6xl mx-auto px-6 py-24">
          <h1 className="font-display text-4xl text-white uppercase">Explainer not found</h1>
          <Link to="/explainers" className="text-cy font-mono text-xs mt-4 inline-block hover:underline">
            &larr; BACK TO EXPLAINERS
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const idx = ARTICLES.findIndex((a) => a.slug === slug);
  const prev = idx > 0 ? ARTICLES[idx - 1] : null;
  const next = idx < ARTICLES.length - 1 ? ARTICLES[idx + 1] : null;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    inLanguage: "en-AU",
    author: {
      "@type": "Organization",
      name: "PrivateCredit.com.au",
      url: "https://privatecredit.com.au/",
    },
    publisher: {
      "@type": "Organization",
      name: "PrivateCredit.com.au",
      url: "https://privatecredit.com.au/",
    },
    mainEntityOfPage: `https://privatecredit.com.au/explainers/${article.slug}`,
  };

  return (
    <div className="bg-navy text-ink min-h-screen antialiased" data-testid="article-page">
      <Seo title={article.title} description={article.description} path={`/explainers/${article.slug}`} />
      <JsonLd data={articleSchema} />
      <Nav />
      <main className="max-w-3xl mx-auto px-6 py-16">
        <Link
          to="/explainers"
          data-testid="article-back-link"
          className="text-cy font-mono text-xs inline-flex items-center gap-1 hover:underline"
        >
          <ArrowLeft className="w-3 h-3" /> ALL EXPLAINERS
        </Link>

        <div className="flex items-center gap-3 mt-8 text-xs font-mono text-cy font-bold">
          <span>{article.tag}</span>
          <span className="text-slate-500 font-normal inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {article.readMinutes} MIN READ
          </span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl text-white uppercase mt-3 leading-tight">
          {article.title}
        </h1>
        <p className="text-base md:text-lg text-mut leading-relaxed mt-4">{article.excerpt}</p>

        <div className="mt-10 space-y-10" data-testid="article-body">
          {article.body.map((section) => (
            <section key={section.h}>
              <h2 className="font-display text-2xl text-white uppercase">{section.h}</h2>
              {section.paragraphs.map((p, i) => (
                <p key={i} className="text-base text-mut font-body leading-relaxed mt-4">
                  {p}
                </p>
              ))}
            </section>
          ))}
        </div>

        <div className="mt-12 p-6 rounded-xl bg-surface border border-line" data-testid="article-advice-note">
          <p className="text-[11px] leading-relaxed text-slate-400 font-body">
            <strong className="text-slate-200">General Advice Warning:</strong> This explainer is factual and
            general in nature only. It does not constitute financial, investment, or legal advice, and has been
            prepared without taking into account the objectives, financial situation, or needs of any person.
            Obtain independent professional advice before making any financial decision.
          </p>
        </div>

        <div className="mt-8 p-6 rounded-xl bg-surface border border-line flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-xs font-mono text-cy uppercase tracking-widest">[PORTAL] BORROWER ROUTING</div>
            <p className="text-sm text-mut font-body mt-1">
              Need private funding for a business or project? Enquiries route to Grow Capital.
            </p>
          </div>
          <Link
            to="/#terminal-enquiry"
            data-testid="article-enquiry-cta"
            className="px-6 py-3 rounded bg-cy text-navy font-mono text-xs font-bold hover:bg-cy-soft transition-colors whitespace-nowrap"
          >
            SUBMIT FUNDING ENQUIRY
          </Link>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row justify-between gap-4 font-mono text-xs">
          {prev ? (
            <Link
              to={`/explainers/${prev.slug}`}
              data-testid="article-prev-link"
              className="text-slate-400 hover:text-cy transition-colors inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> {prev.title.toUpperCase()}
            </Link>
          ) : (
            <span />
          )}
          {next && (
            <Link
              to={`/explainers/${next.slug}`}
              data-testid="article-next-link"
              className="text-slate-400 hover:text-cy transition-colors inline-flex items-center gap-1 sm:ml-auto"
            >
              {next.title.toUpperCase()} <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ArticlePage;
